export type CaseStudy = {
  slug: string;
  label: string;
  title: string;
  pageTitle: string;
  summary: string;
  cardDescription: string;
  meta: Array<{ label: string; value: string }>;
  highlights: Array<{ value: string; label: string; detail: string }>;
  problemTitle: string;
  problemBody: string;
  scenarioPatterns: string[];
  howWeHelped: string[];
  optimizedState: Array<{ title: string; body: string }>;
  protectionCases: string[];
  impactStatement: string;
};

export const caseStudies: CaseStudy[] = [
  {
    slug: 'luxury-goods-resale',
    label: 'Luxury goods resale',
    title: 'Recover checkout conversion without giving up risk control',
    pageTitle: 'How a luxury resale platform recovered checkout conversion without giving up risk control',
    summary:
      'This luxury resale platform was bringing shoppers all the way to payment and still losing too many first purchases. Failed transactions were driving churn even above the already-punishing 40% benchmark, turning checkout failure into a direct growth problem. Because order values were high, the team needed to recover conversion without opening the door to more chargebacks, NSF exposure, and network declines.',
    cardDescription:
      'A luxury resale platform moved checkout conversion from 57% to 72% without giving up the risk controls required for high-value transactions.',
    meta: [
      { label: 'Segment', value: 'Luxury goods resale' },
      { label: 'Engagement', value: 'Completed pilot' },
      { label: 'Pilot', value: 'US/CA · Sept 2025 · $50M GMV' },
      { label: 'Measurement', value: 'Measured against the merchant\'s own baseline' },
      { label: 'Focus', value: 'Checkout conversion and risk balance on high-value transactions' },
    ],
    highlights: [
      {
        value: '57% -> 72%',
        label: 'Checkout conversion',
        detail: 'Checkout conversion rose 15 points while high-value risk controls stayed intact.',
      },
      {
        value: '>40%',
        label: 'First-time user churn after failure',
        detail: 'Failed first purchases were driving churn even above the 40% benchmark, making payment failure a growth issue.',
      },
      {
        value: 'High-value baskets protected',
        label: 'Risk stayed in view',
        detail: 'The business could not simply tear down risk controls because the baskets were too valuable and the downside was too large.',
      },
    ],
    problemTitle: 'Checkout failure was turning acquisition spend into churn',
    problemBody:
      'Checkout conversion had fallen to 57%, which meant too many shoppers were failing at the final payment step after the business had already done the hard work of acquiring them. That made payment failure a direct drag on growth and customer acquisition efficiency. At the same time, the team could not simply loosen controls to chase approval rate because basket sizes were high and the downside from chargebacks, NSF exposure, and network declines was too large.',
    scenarioPatterns: [
      'The historical file did not represent one generic ecommerce checkout. Mobile wallet-first traffic, higher-ticket financing shoppers, and risk-managed card flows were behaving differently enough to require different payment responses.',
      'Lower-friction mobile buyers needed a stack that emphasized fast completion, while higher-ticket baskets justified BNPL or other supporting methods only when affordability materially improved completion.',
      'Risk-managed checkout flows required Card to stay present and route-optimized, but not to dominate the visible stack in every payment context.',
    ],
    howWeHelped: [
      'Read the payment environment as distinct payment moments instead of optimizing one generic checkout path.',
      'Optimized against the company’s real conversion-versus-risk balance, not a generic “higher approval always wins” rule.',
      'Returned one visible checkout stack plus one optimized card route for each payment context so the customer experience and the underlying route logic stayed aligned.',
    ],
    optimizedState: [
      {
        title: 'Current state',
        body: 'The business was treating high-value, wallet-friendly, and risk-managed checkout moments too similarly, which forced one compromise-heavy payment setup onto materially different payment contexts.',
      },
      {
        title: 'Optimized state',
        body: 'Ormal separated those behaviors into distinct payment contexts and returned a stack that changed by device, basket size, and risk posture while still keeping Card present with a concrete optimized route.',
      },
      {
        title: 'Commercial result',
        body: 'Checkout conversion increased by 15 points, from 57% to 72%, while the business kept the controls needed for high-value transactions instead of buying approval with looser risk policy.',
      },
    ],
    protectionCases: [
      'Ormal kept risk-managed payment contexts explicit so control logic did not get watered down by the broader ecommerce mix.',
      'Higher-ticket financing options were elevated only when the payment context supported them, rather than sprayed across normal checkout by default.',
      'Card remained mandatory, but the route underneath Card could still be optimized separately from the visible stack emphasis.',
    ],
    impactStatement:
      'For this business, payment failure was not a narrow authorization issue. It was a context-specific growth problem. Once the company stopped treating unlike checkout moments as if they needed the same answer, Ormal improved conversion, preserved risk discipline, and gave the team a clearer operating model for future refreshes.',
  },
  {
    slug: 'marketplace-multinational',
    label: 'Marketplace multinational',
    title: 'Reduce chargeback pressure and blended payment cost',
    pageTitle: 'How a multinational marketplace lowered chargeback pressure and payment cost across key markets',
    summary:
      'This marketplace was carrying too much cost and too much chargeback pressure in markets that mattered most. FX treatment, processor mix, and routing decisions were inflating blended payment cost, while several markets were at risk of staying inside elevated card-network programs. The team needed to lower cost and regain chargeback control without shrinking checkout coverage or making the marketplace harder to buy through.',
    cardDescription:
      'A multinational marketplace pushed chargebacks below 0.5% in key markets and cut blended payment cost by 8% without narrowing checkout coverage.',
    meta: [
      { label: 'Segment', value: 'Marketplace multinational' },
      { label: 'Engagement', value: 'Completed pilot' },
      { label: 'Pilot', value: 'International · Oct–Nov 2025 · $350M GMV' },
      { label: 'Measurement', value: 'Measured against the merchant\'s own baseline' },
      { label: 'Focus', value: 'Chargeback pressure, FX drag, and blended payment cost' },
    ],
    highlights: [
      {
        value: '1.5% → <0.5%',
        label: 'Chargebacks in key markets',
        detail: 'Chargeback rate fell from 1.5% to below 0.5% in key markets, moving the business out of elevated card-network programs.',
      },
      {
        value: '-8%',
        label: 'Blended payment cost',
        detail: 'Payment cost fell 8% after Ormal rebalanced FX, routing, and payment mix across the stack.',
      },
      {
        value: 'Coverage stayed broad',
        label: 'Checkout stayed sellable',
        detail: 'The marketplace kept broad buyer coverage while the stack underneath became cheaper and more controlled.',
      },
    ],
    problemTitle: 'FX drag and chargeback pressure were hitting the same markets at the same time',
    problemBody:
      'The business was paying too much to move money through the stack while also carrying elevated chargeback rates in several important markets. This was not one processor problem or one pricing problem. FX treatment, processor choice, method mix, and chargeback exposure were interacting across the full marketplace flow. The team needed to lower blended payment cost and get chargebacks below card-network pressure thresholds without cutting back checkout coverage or slowing growth.',
    scenarioPatterns: [
      'The payment file grouped into materially different cross-border and local-buyer patterns instead of one uniform marketplace checkout.',
      'Local payment methods, FX treatment, and card-route economics were not equally relevant across all markets, so one global rule set was creating unnecessary drag.',
      'Commercial-pressure states mattered in their own right because chargeback thresholds and fee leakage were changing what the best answer should be in key markets.',
    ],
    howWeHelped: [
      'Built payment-response logic that reflected market-by-market payment behavior instead of treating the marketplace as one checkout.',
      'Optimized each payment context against the company’s balance of conversion, cost, and risk while keeping FX, chargeback, and coverage pressure in the same decision frame.',
      'Returned visible stacks and card-route decisions that preserved local relevance for buyers while improving the economics underneath the flow.',
    ],
    optimizedState: [
      {
        title: 'Current state',
        body: 'The company was using a checkout and routing posture that looked broad on paper but was forcing too many unlike markets through the same economic and risk assumptions.',
      },
      {
        title: 'Optimized state',
        body: 'Ormal separated local-market and cross-border behaviors, changed how payment methods and card routes were used market by market, and made company-pressure states explicit instead of burying them inside the default flow.',
      },
      {
        title: 'Commercial result',
        body: 'Chargeback pressure fell below 0.5% in key markets and blended payment cost fell 8% while the checkout remained broad and sellable across the marketplace footprint.',
      },
    ],
    protectionCases: [
      'Protection cases kept chargeback-sensitive markets from distorting the recommendation for healthy everyday traffic.',
      'FX and fee pressure were treated as decision inputs that could justify a different answer where commercial drag became material.',
      'Local payment coverage stayed visible where it mattered without forcing every market into the same local-method posture.',
    ],
    impactStatement:
      'This marketplace did not need fewer payment options. It needed a better payment decision system. Once Ormal separated local behavior from pressure-driven exceptions and optimized the stack underneath those differences, the business lowered chargeback pressure, reduced blended cost, and kept checkout coverage broad where it needed to stay broad.',
  },
];

export const getCaseStudyBySlug = (slug: string) => caseStudies.find((item) => item.slug === slug);
